import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  useTheme,
  useMediaQuery,
  ListSubheader
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Logout as LogoutIcon,
  AccessTime as TimeIcon,
  Support as SupportIcon,
  Person as TalentIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { logout } from '../../services/authService';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import useAdminStatus from '../../hooks/useAdminStatus';

const drawerWidth = 240;
const miniDrawerWidth = 72;

const Sidebar = ({ open, setOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUser = useCurrentUser();
  const { isAdmin, loading: adminLoading } = useAdminStatus();

  const handleLogout = async () => {
    await logout();
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  // Top-level navigation items (same level as section headers)
  const topLevelItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' }
  ];

  const menuSections = [
    {
      title: 'CRM',
      items: [
        { text: 'Opportunities', icon: <TrendingUpIcon />, path: '/opportunities' },
        { text: 'Companies', icon: <BusinessIcon />, path: '/companies' },
        { text: 'Contacts', icon: <PeopleIcon />, path: '/contacts' },
      ]
    },
    {
      title: 'Operation',
      items: [
        { text: 'Time Tracker', icon: <TimeIcon />, path: '/time-tracker' },
        { text: 'Support Ticket', icon: <SupportIcon />, path: '/support-contracts' },
        { text: 'Talent Pool', icon: <TalentIcon />, path: '/talents' },
        ...(isAdmin ? [{ text: 'Admin', icon: <AdminIcon />, path: '/admin' }] : [])
      ]
    }
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar />
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {/* Top-level items (Dashboard) */}
        <List sx={{ pb: 1 }}>
          {topLevelItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton 
                component={Link} 
                to={item.path}
                onClick={handleDrawerClose}
                sx={!open && !isMobile ? {
                  minHeight: 48,
                  justifyContent: 'center',
                  px: 2.5,
                } : {
                  pl: 2
                }}
              >
                <ListItemIcon sx={!open && !isMobile ? {
                  minWidth: 0,
                  justifyContent: 'center'
                } : {}}>
                  {item.icon}
                </ListItemIcon>
                {(open || isMobile) && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Divider between top-level items and sections */}
        {(open || isMobile) && <Divider sx={{ mx: 2, mb: 1 }} />}

        {/* Section-based menu items */}
        {menuSections.map((section, sectionIndex) => (
          <List
            key={section.title}
            subheader={
              (open || isMobile) ? (
                <ListSubheader
                  component="div"
                  sx={{
                    backgroundColor: 'transparent',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    lineHeight: '32px',
                    px: 2
                  }}
                >
                  {section.title}
                </ListSubheader>
              ) : null
            }
            sx={{
              pb: sectionIndex < menuSections.length - 1 ? 1 : 0,
              '&:not(:last-child)': {
                borderBottom: (open || isMobile) ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                mb: 1
              }
            }}
          >
            {section.items.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton 
                  component={Link} 
                  to={item.path}
                  onClick={handleDrawerClose}
                  sx={!open && !isMobile ? {
                    minHeight: 48,
                    justifyContent: 'center',
                    px: 2.5,
                  } : {
                    pl: 3
                  }}
                >
                  <ListItemIcon sx={!open && !isMobile ? {
                    minWidth: 0,
                    justifyContent: 'center'
                  } : {}}>
                    {item.icon}
                  </ListItemIcon>
                  {(open || isMobile) && <ListItemText primary={item.text} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ))}
      </Box>
      
      <Box sx={{ mt: 'auto' }}>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={handleLogout}
              sx={!open && !isMobile ? {
                minHeight: 48,
                justifyContent: 'center',
                px: 2.5,
              } : {}}
            >
              <ListItemIcon sx={!open && !isMobile ? {
                minWidth: 0,
                justifyContent: 'center'
              } : {}}>
                <LogoutIcon />
              </ListItemIcon>
              {(open || isMobile) && <ListItemText primary="Logout" />}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : miniDrawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : miniDrawerWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default Sidebar; 